import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MUIDataTable, { MUIDataTableColumnDef, MUIDataTableOptions } from 'mui-datatables';
import dayjs, { Dayjs } from 'dayjs';
import { displayTime, displayYearMonth } from '../util/TimeUtil';
import { getDatatableConfig } from '../util/LocalStorageUtil';
import { getFullName } from '../util/DataUtil';
import { SimpleLoading } from './Loading';
import { NoMatches } from './Datatable';

export type EmailTypeFilter = 'ALL' | EmailLogType;

export function summariseEmailLogs(logs: EmailLog[], now: Dayjs = dayjs()) {
  const since = now.subtract(30, 'day');
  return {
    total: logs.length,
    automatic: logs.filter(log => log.type === 'AutoReminder').length,
    manual: logs.filter(log => log.type === 'ManualReminder').length,
    last30Days: logs.filter(log => dayjs.utc(log.dateSent).isAfter(since)).length,
  };
}

export function filterEmailLogs(logs: EmailLog[], type: EmailTypeFilter) {
  return type === 'ALL' ? logs : logs.filter(log => log.type === type);
}

const FILTERS: { value: EmailTypeFilter; label: string; count: (s: ReturnType<typeof summariseEmailLogs>) => number }[] = [
  { value: 'ALL', label: 'All', count: s => s.total },
  { value: 'AutoReminder', label: 'Automatic', count: s => s.automatic },
  { value: 'ManualReminder', label: 'Manual', count: s => s.manual },
];

export default function EmailHistory(props: { emailLogs: EmailLog[] | null; loading: boolean; onRefresh: () => void }) {
  const { emailLogs, loading, onRefresh } = props;
  const [typeFilter, setTypeFilter] = useState<EmailTypeFilter>('ALL');

  const refreshButton = (
    <Button size="small" startIcon={<RefreshIcon />} onClick={onRefresh} disabled={loading}>
      Refresh
    </Button>
  );

  if (emailLogs == null || (loading && emailLogs.length === 0)) {
    return <SimpleLoading message="Loading email history" />;
  }

  if (emailLogs.length === 0) {
    return (
      <Alert severity="info" action={refreshButton}>
        No reminder emails have been sent yet. Select clients on the dashboard and choose <b>Send reminder</b>, or turn on automatic reminders on the
        Organisations page.
      </Alert>
    );
  }

  const summary = summariseEmailLogs(emailLogs);
  const rows = filterEmailLogs(emailLogs, typeFilter);

  const column = (name: keyof EmailLog, label: string, render: (log: EmailLog) => React.ReactNode, extra: object = {}): MUIDataTableColumnDef => ({
    name,
    label,
    options: { filter: false, sort: false, customBodyRenderLite: (dataIndex: number) => render(rows[dataIndex]), ...extra },
  });

  const columns: MUIDataTableColumnDef[] = [
    column(
      'dateSent',
      'Sent',
      log => (
        <Tooltip title={displayTime(log.dateSent)}>
          <span style={{ whiteSpace: 'nowrap' }}>{dayjs.utc(log.dateSent).fromNow()}</span>
        </Tooltip>
      ),
      { sortDirection: 'desc' },
    ),
    column('recipientOrganisationName', 'Organisation', log => <b style={{ fontWeight: 500 }}>{log.recipientOrganisationName}</b>),
    column('recipientName', 'Recipient', log => (
      <>
        {log.recipientName}
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {log.recipientEmail}
        </Typography>
      </>
    )),
    column('yearMonth', 'BAS month', log => displayYearMonth(log.yearMonth)),
    column('templateName', 'Template', log => log.templateName || 'Default'),
    column('type', 'Type', log =>
      log.type === 'AutoReminder' ? (
        <Chip size="small" label="Automatic" variant="outlined" color="info" />
      ) : (
        <Chip size="small" label={`Manual · ${log.creator ? getFullName(log.creator) : 'unknown'}`} variant="outlined" />
      ),
    ),
  ];

  const options: MUIDataTableOptions = {
    enableNestedDataAccess: '.',
    rowsPerPageOptions: [25, 50, 100],
    rowsPerPage: getDatatableConfig('emailsLogConfig', 'rowsPerPage') || 25,
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    storageKey: 'emailsLogConfig',
    selectableRowsHideCheckboxes: true,
    fixedHeader: true,
    elevation: 0,
    searchPlaceholder: 'Search by organisation, recipient or template',
    textLabels: {
      body: {
        noMatch: <NoMatches message="No emails match" onClear={typeFilter !== 'ALL' ? () => setTypeFilter('ALL') : undefined} clearLabel="Show all emails" />,
      },
    },
    setTableProps: () => ({ size: 'small' }),
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          <b>{summary.last30Days}</b> sent in the last 30 days · {summary.total} in total
        </Typography>
        <Stack direction="row" gap={1} role="group" aria-label="Filter by type">
          {FILTERS.map(filter => (
            <Chip
              key={filter.value}
              size="small"
              label={`${filter.label} · ${filter.count(summary)}`}
              color={typeFilter === filter.value ? 'primary' : 'default'}
              variant={typeFilter === filter.value ? 'filled' : 'outlined'}
              aria-pressed={typeFilter === filter.value}
              onClick={() => setTypeFilter(filter.value)}
            />
          ))}
        </Stack>
        <Box sx={{ ml: 'auto' }}>{refreshButton}</Box>
      </Stack>
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', opacity: loading ? 0.6 : 1 }}>
        <MUIDataTable title="" data={rows} columns={columns} options={options} />
      </Box>
    </Box>
  );
}
